import * as React from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Link, MenuItem, Radio, RadioGroup, Snackbar, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    DashboardAppSettingDto, getMatchBriefExecutions, getMatchBriefs, getMatchResearches, getPaginatedSports,
    listDashboardAppSettings, putDashboardAppSetting, triggerMatchBrief,
} from '../Api';
import {
    MATCH_BRIEF_SETTING_KEYS, MATCH_BRIEF_STAGES, MatchBriefDto, MatchBriefExecutionDto, MatchBriefExecutionPageDto,
    MatchBriefExecutionStatus, MatchBriefScope, MatchBriefStatus, MatchResearchDto, MatchResearchPageDto,
} from '../models/dtos/MatchBriefDto';
import SportEntityDto from '../models/dtos/SportEntityDto';
import MatchBriefDialog, { matchBriefStatusChip, matchBriefTitle } from '../components/match-briefs/MatchBriefDialog';
import MatchBriefExecutionDialog, {
    briefSourceLabel, briefWorkflowOf, executionMatchTitle, MATCH_BRIEF_WORKFLOW, MatchBriefStageChip, SCOPE_LABELS, STAGE_LABELS,
} from '../components/match-briefs/MatchBriefExecutionDialog';
import MatchResearchDialog, { matchResearchConfirmedChip, matchResearchTitle } from '../components/match-briefs/MatchResearchDialog';
import { formatAiDuration, formatAiUsd } from '../components/ai/AiShared';

interface SettingsForm {
    enabled: boolean;
    sportIds: number[];
    maxMatches24h: string;
    maxMatchesPerRun: string;
    maxFailures24h: string;
    intervalMinutes: string;
    windowHours: string;
    minBets: string;
    researchMaxAgeHours: string;
}

type NumberKey = 'maxMatches24h' | 'maxMatchesPerRun' | 'maxFailures24h' | 'intervalMinutes' | 'windowHours' | 'minBets' | 'researchMaxAgeHours';

type Summary = Pick<MatchBriefExecutionPageDto, 'failures_24h' | 'max_failures_24h' | 'running'>;

const DEFAULTS: SettingsForm = {
    enabled: false, sportIds: [22], maxMatches24h: '10', maxMatchesPerRun: '10', maxFailures24h: '3', intervalMinutes: '60',
    windowHours: '36', minBets: '500', researchMaxAgeHours: '6',
};

const NUMBER_FIELDS: { key: NumberKey; label: string; help: string; min: number; max: number }[] = [
    { key: 'maxMatches24h', label: 'Max matches within 24 h', help: 'The scheduler stops once this many briefs cover matches starting in the next 24 hours.', min: 0, max: 200 },
    { key: 'maxMatchesPerRun', label: 'Max matches per run', help: 'At most this many triggers per scheduled run; the rest wait for the next interval.', min: 0, max: 200 },
    { key: 'maxFailures24h', label: 'Max failures in 24 h', help: 'Once this many scheduled invocations failed in 24 hours, the scheduler sends no new trigger. Manual triggers neither count nor stop.', min: 0, max: 1000 },
    { key: 'intervalMinutes', label: 'Interval (minutes)', help: 'Time between two scheduled runs; checked every 5 minutes.', min: 5, max: 1440 },
    { key: 'windowHours', label: 'Window (hours)', help: 'Matches starting within this many hours are considered.', min: 1, max: 168 },
    { key: 'minBets', label: 'Min bets', help: 'Matches with fewer bets are skipped.', min: 0, max: 1_000_000 },
    { key: 'researchMaxAgeHours', label: 'Reuse research (hours)', help: 'A prediction reuses the match\'s web research up to this old (if it found the fixture) instead of searching again, and the scheduler picks such matches first; 0 = always research.', min: 0, max: 168 },
];

const SETTING_KEY: Record<NumberKey, string> = {
    maxMatches24h: MATCH_BRIEF_SETTING_KEYS.maxMatches24h,
    maxMatchesPerRun: MATCH_BRIEF_SETTING_KEYS.maxMatchesPerRun,
    maxFailures24h: MATCH_BRIEF_SETTING_KEYS.maxFailures24h,
    intervalMinutes: MATCH_BRIEF_SETTING_KEYS.intervalMinutes,
    windowHours: MATCH_BRIEF_SETTING_KEYS.windowHours,
    minBets: MATCH_BRIEF_SETTING_KEYS.minBets,
    researchMaxAgeHours: MATCH_BRIEF_SETTING_KEYS.researchMaxAgeHours,
};

function formFromSettings(rows: DashboardAppSettingDto[]): SettingsForm {
    const byKey = new Map(rows.map(r => [r.key, r]));
    const decimal = (key: NumberKey) => {
        const v = byKey.get(SETTING_KEY[key])?.decimalValue;
        return v == null ? DEFAULTS[key] : String(v);
    };
    const sports = byKey.get(MATCH_BRIEF_SETTING_KEYS.sportIds)?.stringValue;
    return {
        enabled: byKey.get(MATCH_BRIEF_SETTING_KEYS.enabled)?.booleanValue ?? DEFAULTS.enabled,
        sportIds: sports == null ? DEFAULTS.sportIds : sports.split(/[,\s]+/).map(Number).filter(n => Number.isInteger(n) && n > 0),
        maxMatches24h: decimal('maxMatches24h'),
        maxMatchesPerRun: decimal('maxMatchesPerRun'),
        maxFailures24h: decimal('maxFailures24h'),
        intervalMinutes: decimal('intervalMinutes'),
        windowHours: decimal('windowHours'),
        minBets: decimal('minBets'),
        researchMaxAgeHours: decimal('researchMaxAgeHours'),
    };
}

/**
 * Matches › AI predictions (betup-server-api docs/match-briefs.md): the settings (generic `match_brief.*` app settings
 * read by Crons on every run; they limit the scheduler only), "Run now" (one manual trigger to Crons' brief queue: a
 * prediction, or a web research only), every invocation with its stage — newest first, or by status — with its AI
 * workflow run, the briefs and the match researches. `?match=<id>` narrows invocations and researches to one match.
 */
export default function MatchBriefsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const tab = tabParam === 'briefs' || tabParam === 'researches' ? tabParam : 'invocations';
    const matchFilter = Number(searchParams.get('match')) || null;
    const setTab = (value: string) => {
        const next: Record<string, string> = {};
        if (value !== 'invocations') next.tab = value;
        if (matchFilter) next.match = String(matchFilter);
        setSearchParams(next, { replace: true });
    };
    const clearMatchFilter = () => setSearchParams(tab === 'invocations' ? {} : { tab }, { replace: true });

    const [form, setForm] = React.useState<SettingsForm>(DEFAULTS);
    const [saved, setSaved] = React.useState<SettingsForm>(DEFAULTS);
    const [settingsError, setSettingsError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [sports, setSports] = React.useState<SportEntityDto[]>([]);
    const [toast, setToast] = React.useState<string | null>(null);
    const [runNowOpen, setRunNowOpen] = React.useState(false);
    const [summary, setSummary] = React.useState<Summary | null>(null);
    const [executionsVersion, setExecutionsVersion] = React.useState(0);

    React.useEffect(() => {
        listDashboardAppSettings('match_brief.').then(result => {
            if (result == null) {
                setSettingsError('The settings could not be loaded; the defaults are shown.');
                return;
            }
            const loaded = formFromSettings(result);
            setForm(loaded);
            setSaved(loaded);
        });
        getPaginatedSports(0, 500).then(page => setSports(page?.items ?? []));
    }, []);

    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    const save = async () => {
        for (const f of NUMBER_FIELDS) {
            const n = Number(form[f.key]);
            if (form[f.key].trim() === '' || !Number.isInteger(n) || n < f.min || n > f.max) {
                setSettingsError(`${f.label} must be a whole number from ${f.min} to ${f.max}.`);
                return;
            }
        }
        setSaving(true);
        setSettingsError(null);
        type Write = [string, Parameters<typeof putDashboardAppSetting>[1]];
        const writes: Write[] = [
            [MATCH_BRIEF_SETTING_KEYS.enabled, { valueType: 'BOOLEAN', booleanValue: form.enabled }],
            [MATCH_BRIEF_SETTING_KEYS.sportIds, { valueType: 'STRING', stringValue: form.sportIds.join(',') }],
            ...NUMBER_FIELDS.map((f): Write => [SETTING_KEY[f.key], { valueType: 'DECIMAL', decimalValue: Number(form[f.key]) }]),
        ];
        for (const [key, body] of writes) {
            const { error } = await putDashboardAppSetting(key, body);
            if (error) {
                setSettingsError(`${key}: ${error}`);
                setSaving(false);
                return;
            }
        }
        setSaved(form);
        setSaving(false);
        setToast('Settings saved; Crons uses them on its next run');
    };

    const sportName = (id: number) => sports.find(s => s.id === id)?.name ?? `Sport ${id}`;
    const stopped = summary != null && summary.failures_24h >= summary.max_failures_24h;

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" alignItems="flex-start" gap={2}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4">AI predictions</Typography>
                    <Typography variant="body2" color="text.secondary">
                        AI briefs for upcoming matches. Each trigger is one invocation in Crons, one at a time: it picks a match,
                        collects its context, a researcher agent reads the web, a synthesis task merges it with BETUP odds, crowd
                        and form, and the brief is translated. Every AI call of an invocation shares its correlation id.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={() => setRunNowOpen(true)}>Run now</Button>
            </Stack>

            {stopped && (
                <Alert severity="warning">
                    {summary!.failures_24h} scheduled invocations failed in the last 24 hours (limit {summary!.max_failures_24h}):
                    the scheduler sends no new trigger until the count drops or the limit is raised. Manual triggers still work.
                </Alert>
            )}

            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h6">Settings</Typography>
                        <Button variant="contained" startIcon={<SaveIcon />} disabled={!dirty || saving} onClick={save}>Save</Button>
                    </Stack>
                    {settingsError && <Alert severity="warning" sx={{ mb: 2 }}>{settingsError}</Alert>}
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={<Switch checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />}
                                label={form.enabled ? 'Scheduler on' : 'Scheduler off'} />
                            <Typography variant="caption" color="text.secondary" display="block">
                                Off: the scheduler sends no trigger. Manual triggers still work; ready briefs stay visible.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Autocomplete
                                multiple
                                options={sports.map(s => s.id)}
                                value={form.sportIds}
                                onChange={(_, value) => setForm({ ...form, sportIds: value })}
                                getOptionLabel={id => `${sportName(id)} (${id})`}
                                renderInput={params => <TextField {...params} size="small" label="Sports"
                                    helperText={form.sportIds.length === 0 ? 'No sport: nothing is selected automatically.' : 'Matches of these sports are considered.'}
                                    error={form.sportIds.length === 0} />}
                            />
                        </Grid>
                        {NUMBER_FIELDS.map(f => (
                            <Grid item xs={12} sm={6} md={2} key={f.key}>
                                <TextField size="small" fullWidth label={f.label} value={form[f.key]} helperText={f.help}
                                    onChange={e => setForm({ ...form, [f.key]: e.target.value.replace(/[^\d]/g, '') })} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" gap={2}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1 }}>
                        <Tab value="invocations" label={summary?.running ? `Invocations (${summary.running} running)` : 'Invocations'} />
                        <Tab value="briefs" label="Briefs" />
                        <Tab value="researches" label="Researches" />
                    </Tabs>
                    {matchFilter && tab !== 'briefs' && (
                        <Chip label={`Only match ${matchFilter}`} onDelete={clearMatchFilter} />
                    )}
                </Stack>
            </Box>
            {tab === 'invocations' && <InvocationsTable version={executionsVersion} onSummary={setSummary} matchId={matchFilter} />}
            {tab === 'briefs' && <BriefsTable />}
            {tab === 'researches' && <ResearchesTable matchId={matchFilter} />}

            <RunNowDialog open={runNowOpen} onClose={() => setRunNowOpen(false)}
                onQueued={(matchId, scope) => {
                    setRunNowOpen(false);
                    setToast(scope === 'RESEARCH' ? `Queued: a research of match ${matchId}`
                        : matchId ? `Queued: the brief of match ${matchId}` : 'Queued: the best uncovered match');
                    setTimeout(() => setExecutionsVersion(v => v + 1), 1500);
                }} />
            <Snackbar open={toast != null} autoHideDuration={2500} message={toast} onClose={() => setToast(null)} />
        </Stack>
    );
}

function RunNowDialog({ open, onClose, onQueued }: { open: boolean; onClose: () => void; onQueued: (matchId: number | null, scope: MatchBriefScope) => void }) {
    const [matchId, setMatchId] = React.useState('');
    const [scope, setScope] = React.useState<MatchBriefScope>('BRIEF');
    const [error, setError] = React.useState<string | null>(null);
    const [sending, setSending] = React.useState(false);

    React.useEffect(() => { if (open) { setMatchId(''); setScope('BRIEF'); setError(null); } }, [open]);

    const send = async () => {
        setSending(true);
        setError(null);
        const id = matchId.trim() ? Number(matchId) : null;
        if (scope === 'RESEARCH' && id == null) {
            setError('A research needs a match id.');
            setSending(false);
            return;
        }
        const result = await triggerMatchBrief(id, scope);
        setSending(false);
        if (result.error) setError(result.error);
        else onQueued(id, scope);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Run now</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sends one manual trigger to Crons; the scheduler's limits (switched off, 24-hour cap, max failures) don't
                    apply. A prediction with a match id writes (or rewrites) that match's brief; empty, it takes the best
                    uncovered match the settings select. A research only searches the web for the match again. It waits
                    behind triggers already in the queue.
                </Typography>
                <RadioGroup row value={scope} onChange={e => setScope(e.target.value as MatchBriefScope)} sx={{ mb: 1 }}>
                    <FormControlLabel value="BRIEF" control={<Radio size="small" />} label="Prediction" />
                    <FormControlLabel value="RESEARCH" control={<Radio size="small" />} label="Research only" />
                </RadioGroup>
                <TextField fullWidth size="small" label={scope === 'RESEARCH' ? 'Match id' : 'Match id (optional)'} value={matchId} autoFocus
                    onChange={e => setMatchId(e.target.value.replace(/[^\d]/g, ''))}
                    onKeyDown={e => { if (e.key === 'Enter' && !sending) send(); }} />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" startIcon={<PlayArrowIcon />} disabled={sending} onClick={send}>Send trigger</Button>
            </DialogActions>
        </Dialog>
    );
}

/** Invocations newest first (or by status), refreshed every 10 s while one runs. */
function InvocationsTable({ version, onSummary, matchId }: { version: number; onSummary: (summary: Summary) => void; matchId: number | null }) {
    const [status, setStatus] = React.useState<MatchBriefExecutionStatus | ''>('');
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [sortModel, setSortModel] = React.useState<GridSortModel>([{ field: 'created_at', sort: 'desc' }]);
    const [page, setPage] = React.useState<MatchBriefExecutionPageDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [openId, setOpenId] = React.useState<number | null>(null);

    const load = React.useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        const sort = sortModel[0]?.field === 'status' ? 'status' : 'created';
        const direction = sortModel[0]?.sort === 'asc' ? 'asc' : 'desc';
        const result = await getMatchBriefExecutions(paginationModel.page * paginationModel.pageSize, paginationModel.pageSize, status || null, sort, direction, matchId);
        setFailed(result == null);
        if (result) {
            setPage(result);
            onSummary({ failures_24h: result.failures_24h, max_failures_24h: result.max_failures_24h, running: result.running });
        }
        setLoading(false);
    }, [paginationModel, sortModel, status, onSummary, matchId]);

    React.useEffect(() => { load(); }, [load, version]);

    // While an invocation runs its stage changes every few seconds: follow it.
    React.useEffect(() => {
        if (!page?.running) return;
        const timer = setInterval(() => load(true), 10_000);
        return () => clearInterval(timer);
    }, [page?.running, load]);

    const columns: GridColDef<MatchBriefExecutionDto>[] = [
        { field: 'created_at', headerName: 'Received', width: 170, valueGetter: p => new Date(p.row.created_at).toLocaleString() },
        {
            field: 'source', headerName: 'Source', width: 170, sortable: false,
            renderCell: p => (
                <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
                    <Typography variant="body2" component="div">
                        {briefSourceLabel(p.row.source)}
                        {p.row.scope === 'RESEARCH' && <Chip size="small" variant="outlined" color="secondary" label={SCOPE_LABELS.RESEARCH} sx={{ ml: 0.75, height: 20 }} />}
                    </Typography>
                    {p.row.requested_by && (/^user:\d+$/.test(p.row.requested_by)
                        ? <Link variant="caption" component={RouterLink} to={`/users/${p.row.requested_by.slice(5)}`}>player #{p.row.requested_by.slice(5)}</Link>
                        : <Typography variant="caption" color="text.secondary">{p.row.requested_by}</Typography>)}
                </Box>
            ),
        },
        {
            field: 'match', headerName: 'Match', flex: 1, minWidth: 220, sortable: false,
            renderCell: p => p.row.match
                ? (
                    <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
                        <Typography variant="body2">{executionMatchTitle(p.row)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {p.row.match.league ?? `match ${p.row.match.id}`}{p.row.match.start ? ` · ${new Date(p.row.match.start).toLocaleString()}` : ''}
                        </Typography>
                    </Box>
                )
                : <Typography variant="body2" color="text.secondary">{p.row.requested_match_id ? `match ${p.row.requested_match_id}` : '—'}</Typography>,
        },
        {
            field: 'status', headerName: 'Status', width: 210,
            renderCell: p => (
                <Stack direction="row" gap={0.5} alignItems="center">
                    <MatchBriefStageChip status={p.row.status} />
                    {p.row.failed_stage && <Typography variant="caption" color="error">at {STAGE_LABELS[p.row.failed_stage]}</Typography>}
                </Stack>
            ),
        },
        {
            field: 'outcome', headerName: 'Note / error', flex: 1, minWidth: 220, sortable: false,
            renderCell: p => {
                const text = p.row.error ?? p.row.note;
                return text
                    ? <Typography variant="body2" color={p.row.error ? 'error' : 'text.secondary'} noWrap title={text}>{text}</Typography>
                    : '—';
            },
        },
        { field: 'duration', headerName: 'Duration', width: 100, sortable: false, valueGetter: p => p.row.finished_at ? formatAiDuration(p.row.created_at, p.row.finished_at) : '…' },
        { field: 'cost_usd', headerName: 'AI cost', width: 100, sortable: false, valueGetter: p => p.row.cost_usd == null ? '—' : formatAiUsd(p.row.cost_usd) },
        {
            field: 'correlation_id', headerName: 'AI run', width: 160, sortable: false,
            renderCell: p => p.row.correlation_id
                ? <Button size="small" component={RouterLink} onClick={e => e.stopPropagation()} sx={{ textTransform: 'none', fontFamily: 'monospace' }}
                    to={`/ai/workflows/${briefWorkflowOf(p.row.source)}/runs/${encodeURIComponent(p.row.correlation_id)}`}>{p.row.correlation_id}</Button>
                : '—',
        },
    ];

    return (
        <Stack gap={1.5}>
            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    Every trigger taken by Crons, newest first. Sort by Status to group them by stage. Open one for its stages and AI calls.
                </Typography>
                <TextField select size="small" label="Status" value={status} sx={{ width: 200 }}
                    onChange={e => { setStatus(e.target.value as MatchBriefExecutionStatus | ''); setPaginationModel(m => ({ ...m, page: 0 })); }}>
                    <MenuItem value="">All</MenuItem>
                    {MATCH_BRIEF_STAGES.map(s => <MenuItem key={s} value={s}>{STAGE_LABELS[s]}</MenuItem>)}
                </TextField>
                <Button startIcon={<RefreshIcon />} onClick={() => load()}>Refresh</Button>
            </Stack>
            {failed && <Alert severity="error">The invocations could not be loaded.</Alert>}
            <DataGrid
                autoHeight
                rows={page?.items ?? []}
                columns={columns}
                rowCount={page?.totalItems ?? 0}
                loading={loading}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={model => setSortModel(model.length ? model : [{ field: 'created_at', sort: 'desc' }])}
                pageSizeOptions={[20, 50, 100]}
                disableRowSelectionOnClick
                disableColumnMenu
                getRowHeight={() => 'auto'}
                onRowClick={p => setOpenId(p.row.id)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { py: 0.5 } }}
                localeText={{ noRowsLabel: matchId ? 'No invocations for this match' : 'No invocations yet: switch the scheduler on or use Run now' }}
            />
            <MatchBriefExecutionDialog executionId={openId} onClose={() => setOpenId(null)} />
        </Stack>
    );
}

/** The briefs as they were queued, newest first; each opens read-only with previous / next. */
function BriefsTable() {
    const [status, setStatus] = React.useState<MatchBriefStatus | ''>('');
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [rows, setRows] = React.useState<MatchBriefDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [openId, setOpenId] = React.useState<number | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        const page = await getMatchBriefs(paginationModel.page * paginationModel.pageSize, paginationModel.pageSize, status || null);
        setFailed(page == null);
        setRows(page?.items ?? []);
        setRowCount(page?.totalItems ?? 0);
        setLoading(false);
    }, [paginationModel, status]);

    React.useEffect(() => { load(); }, [load]);

    const columns: GridColDef<MatchBriefDto>[] = [
        { field: 'created_at', headerName: 'Created', width: 160, sortable: false, valueGetter: p => new Date(p.row.created_at).toLocaleString() },
        {
            field: 'match', headerName: 'Match', flex: 1, minWidth: 220, sortable: false,
            renderCell: p => (
                <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
                    <Typography variant="body2">{matchBriefTitle(p.row)}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.row.match.league ?? `match ${p.row.match.id}`}</Typography>
                </Box>
            ),
        },
        { field: 'start', headerName: 'Kick-off', width: 160, sortable: false, valueGetter: p => new Date(p.row.match.start).toLocaleString() },
        { field: 'status', headerName: 'Status', width: 150, sortable: false, renderCell: p => matchBriefStatusChip(p.row.status) },
        {
            field: 'headline', headerName: 'Brief', flex: 1.4, minWidth: 260, sortable: false,
            renderCell: p => p.row.headline
                ? <Typography variant="body2" noWrap title={p.row.headline}>{p.row.headline}</Typography>
                : <Typography variant="body2" color={p.row.error ? 'error' : 'text.secondary'} noWrap title={p.row.error ?? undefined}>{p.row.error ?? '—'}</Typography>,
        },
        {
            field: 'pick', headerName: 'Pick', width: 140, sortable: false,
            renderCell: p => p.row.pick ? <Chip size="small" variant="outlined" label={`${p.row.pick}${p.row.confidence ? ` · ${p.row.confidence}` : ''}`} /> : '—',
        },
        { field: 'bets', headerName: 'Bets', width: 90, sortable: false, type: 'number' },
        { field: 'languages', headerName: 'Lang.', width: 70, sortable: false, valueGetter: p => p.row.languages.length + 1 },
    ];

    return (
        <Stack gap={1.5}>
            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>One brief per match; a rewrite replaces it when it succeeds.</Typography>
                <TextField select size="small" label="Status" value={status} sx={{ width: 180 }}
                    onChange={e => { setStatus(e.target.value as MatchBriefStatus | ''); setPaginationModel(m => ({ ...m, page: 0 })); }}>
                    <MenuItem value="">All</MenuItem>
                    {(['READY', 'RESEARCHING', 'FAILED'] as MatchBriefStatus[]).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
                <Button component={RouterLink} to={`/ai/workflows/${MATCH_BRIEF_WORKFLOW}`}>Workflow cost</Button>
            </Stack>
            {failed && <Alert severity="error">The briefs could not be loaded.</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                rowCount={rowCount}
                loading={loading}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[20, 50, 100]}
                disableRowSelectionOnClick
                disableColumnMenu
                getRowHeight={() => 'auto'}
                onRowClick={p => setOpenId(p.row.id)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { py: 0.5 } }}
                localeText={{ noRowsLabel: 'No briefs yet' }}
            />
            <MatchBriefDialog briefId={openId} onClose={() => setOpenId(null)} navigable />
        </Stack>
    );
}

/** Web research runs newest first (qbet_match_research), optionally of one match; each opens with its findings. */
function ResearchesTable({ matchId }: { matchId: number | null }) {
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [page, setPage] = React.useState<MatchResearchPageDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [openId, setOpenId] = React.useState<number | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        const result = await getMatchResearches(paginationModel.page * paginationModel.pageSize, paginationModel.pageSize, matchId);
        setFailed(result == null);
        setPage(result);
        setLoading(false);
    }, [paginationModel, matchId]);

    React.useEffect(() => { load(); }, [load]);

    const columns: GridColDef<MatchResearchDto>[] = [
        { field: 'researched_at', headerName: 'Researched', width: 170, sortable: false, valueGetter: p => new Date(p.row.researched_at).toLocaleString() },
        {
            field: 'match', headerName: 'Match', flex: 1, minWidth: 220, sortable: false,
            renderCell: p => (
                <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
                    <Typography variant="body2">{matchResearchTitle(p.row)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {p.row.match.league ?? `match ${p.row.match.id}`}{p.row.match.start ? ` · ${new Date(p.row.match.start).toLocaleString()}` : ''}
                    </Typography>
                </Box>
            ),
        },
        { field: 'match_confirmed', headerName: 'Fixture', width: 160, sortable: false, renderCell: p => matchResearchConfirmedChip(p.row.match_confirmed) },
        { field: 'findings_count', headerName: 'Findings', width: 90, sortable: false, type: 'number' },
        { field: 'cost_usd', headerName: 'AI cost', width: 100, sortable: false, valueGetter: p => p.row.cost_usd == null ? '—' : formatAiUsd(p.row.cost_usd) },
        {
            field: 'run_id', headerName: 'Research run', width: 130, sortable: false,
            renderCell: p => p.row.run_id
                ? <Button size="small" component={RouterLink} onClick={e => e.stopPropagation()} sx={{ textTransform: 'none', fontFamily: 'monospace' }}
                    to={`/ai/runs/${p.row.run_id}`}>{p.row.run_id.slice(0, 8)}…</Button>
                : '—',
        },
    ];

    return (
        <Stack gap={1.5}>
            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    Every web research of a match, newest first: made by a prediction or on its own from the match page. A
                    prediction reuses its match's latest research while it is fresh.
                </Typography>
                <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
            </Stack>
            {failed && <Alert severity="error">The researches could not be loaded.</Alert>}
            <DataGrid
                autoHeight
                rows={page?.items ?? []}
                columns={columns}
                rowCount={page?.totalItems ?? 0}
                loading={loading}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[20, 50, 100]}
                disableRowSelectionOnClick
                disableColumnMenu
                getRowHeight={() => 'auto'}
                onRowClick={p => setOpenId(p.row.id)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { py: 0.5 } }}
                localeText={{ noRowsLabel: matchId ? 'No research for this match' : 'No researches yet' }}
            />
            <MatchResearchDialog researchId={openId} onClose={() => setOpenId(null)} />
        </Stack>
    );
}
